import { mapApiToPriceItemType } from 'core/accounting/infra/PriceItemTypeMapper'
import { type EntityType } from 'core/domain/EntityType'
import { type Stop } from 'core/domain/Stop'
import { createDate } from 'core/utils/date'
import { isNotEmptyObject } from 'core/utils/object'

import { type Attachment } from '../domain/Attachment'
import { type AwardConfirmation } from '../domain/AwardConfirmation'
import { type AwardedType } from '../domain/AwardedType'
import { type CapacityTimePeriod } from '../domain/CapacityTimePeriod'
import { type Carrier } from '../domain/Carrier'
import { type Currency } from '../domain/Currency'
import { type DistanceUnit } from '../domain/DistanceUnit'
import { type EquipmentSize } from '../domain/EquipmentSize'
import { type EquipmentType } from '../domain/EquipmentType'
import { type ExtraFields } from '../domain/ExtraFields'
import { type Lane } from '../domain/Lane'
import { type LaneProposal } from '../domain/LaneProposal'
import { type LaneProposalErrors } from '../domain/LaneProposalErrors'
import { type MileageSource } from '../domain/MileageSource'
import { type Mode } from '../domain/Mode'
import {
  type AutoSubmit,
  type AwardConfirmationAutoSubmit,
  type Proposal,
} from '../domain/Proposal'
import { type ProposalState } from '../domain/ProposalState'
import { type RateType } from '../domain/RateType'
import { type RequestForProposal } from '../domain/RequestForProposal'
import { type RequestForProposalState } from '../domain/RequestForProposalState'
import {
  mapApiToAccessorial,
  type RawPriceItemTypeWithNotes,
  type RawAccessorial,
} from './AccessorialMapper'

export type RawCarrier = {
  id: number
  dot: string | null
  email: string
  name: string | null
  unsubscribed: boolean
  entity_type?: EntityType
}

export type RawAdditionalStop = {
  city: string
  state: string
  zipcode: string
}

export type RawStop = {
  lane: number
  stop_index: number
  facility_uuid: string | null
  facility_name: string | null
  facility_address: string | null
  city: string
  state: string
  zip_code: string
  country: string
  latitude: string
  longitude: string
}

export type RawLaneProposalErrors = {
  capacity?: string
  flat_rate?: string
  non_field_errors?: string
  per_mile_rate?: string
}

export type RawLaneProposal = {
  id: number
  award_confirmation: AwardConfirmation | null
  awarded_type: AwardedType | null
  awarded_volume: number | null
  capacity: number
  converted_flat_rate: string | null
  converted_per_kilometer_rate: string
  converted_per_mile_rate: string | null
  errors: RawLaneProposalErrors | null
  error_str: string
  flat_rate: string
  lane_id: number
  negotiation_note: string | null
  per_kilometer_rate: string
  per_mile_rate: string
  under_negotiation: boolean
  variance_from_acceptance_rate: string
  lane_preference: string | null
}

export type RawLanePreference = {
  id: string
  mode: Mode
  equipment_type: EquipmentType
  equipment_size?: EquipmentSize
  subequipment_type?: string
  extra_fields?: Record<string, string>
}

export type RawLane = {
  id: number
  additional_stops: Array<RawAdditionalStop>
  dest_city: string | null
  dest_country: string | null
  dest_latitude: string | null
  dest_longitude: string | null
  dest_state: string | null
  dest_timezone: string | null
  dest_zip: string | null
  equipment_type: EquipmentType
  equipment_size?: EquipmentSize | null
  extra_fields: ExtraFields
  lane_id: string
  lane_proposal: Record<string, never> | RawLaneProposal
  lane_proposals: RawLaneProposal[]
  mileage: number
  mileage_kilometers: number
  mileage_source: MileageSource
  notes: string
  origin_city: string | null
  origin_country: string | null
  origin_latitude: string | null
  origin_longitude: string | null
  origin_state: string | null
  origin_timezone: string | null
  origin_zip: string | null
  preferences: RawLanePreference[]
  previous_proposal: number | null
  stops: Array<RawStop>
  target_range: string
  volume: number
}

export type RawAttachment = {
  id: number
  file_name: string
  url: string
}

export type RawRequestForProposal = {
  id: number
  attachments: Array<RawAttachment>
  can_receive_proposals: boolean
  capacity_time_period: CapacityTimePeriod
  contract_end: string
  contract_start: string
  conversion_rate: string | null
  currency: Currency
  deleted: boolean
  distance_type: DistanceUnit
  enable_carrier_feedback?: boolean
  mode: Mode
  name: string
  request_deadline: string
  shipper_email: string
  shipper_logo?: string | null
  shipper_name: string
  shipper_phone: string
  state: RequestForProposalState
  was_finalized: boolean
}

export type RawAutoSubmit = {
  auto_submit: boolean
}

export type RawAwardConfirmationAutoSubmit = {
  award_confirmation_auto_submit: boolean
}

export type RawBackgroundProcess = {
  process: 'upload' | 'submit' | null
  result: 'success' | 'failure' | null
  status: 'running' | 'finished' | null
}

export type RawProposal = {
  accessorials: Array<RawAccessorial>
  requested_accessorials: Array<RawPriceItemTypeWithNotes>
  award_confirmation_auto_submit: boolean
  award_confirmation_deadline: string | null
  award_feedback: string | null
  carrier: RawCarrier
  invite_under_negotiation: boolean
  lanes: Array<RawLane>
  rate_type: RateType
  rfp: RawRequestForProposal
  shipper_company_name: string
  state: ProposalState
  submitted_at: string | null
  suggest_signup: boolean
  unsubscribed: boolean
  background_process: RawBackgroundProcess | null
} & RawAutoSubmit

function handleFalsyValues<T>(value: T) {
  return value || undefined
}

export function mapApiToCarrier(rawCarrier: RawCarrier): Carrier {
  return {
    id: rawCarrier.id,
    dot: handleFalsyValues(rawCarrier.dot),
    email: rawCarrier.email,
    name: handleFalsyValues(rawCarrier.name),
    unsubscribed: rawCarrier.unsubscribed,
    entityType: rawCarrier.entity_type,
  }
}

export function mapApiToAdditionalStop(rawAdditionalStop: RawAdditionalStop): Stop {
  return {
    city: rawAdditionalStop.city,
    state: rawAdditionalStop.state,
    zipCode: rawAdditionalStop.zipcode,
  }
}

export function mapApiToStop(rawAdditionalStop: RawStop): Stop {
  return {
    city: rawAdditionalStop.city,
    state: rawAdditionalStop.state,
    zipCode: rawAdditionalStop.zip_code,
  }
}

export function mapApiToLaneProposalErrors(
  rawLaneProposalErrors: RawLaneProposalErrors | null
): LaneProposalErrors | undefined {
  if (rawLaneProposalErrors != null && isNotEmptyObject(rawLaneProposalErrors)) {
    return {
      capacity: handleFalsyValues(rawLaneProposalErrors.capacity),
      flatRate: handleFalsyValues(rawLaneProposalErrors.flat_rate),
      nonFieldErrors: handleFalsyValues(rawLaneProposalErrors.non_field_errors),
      perMileRate: handleFalsyValues(rawLaneProposalErrors.per_mile_rate),
    }
  }
}

export function mapApiToAutoSubmit({ auto_submit }: RawAutoSubmit): AutoSubmit {
  return { autoSubmit: auto_submit }
}

export function mapApiToAwardConfirmationAutoSubmit({
  award_confirmation_auto_submit,
}: RawAwardConfirmationAutoSubmit): AwardConfirmationAutoSubmit {
  return { awardConfirmationAutoSubmit: award_confirmation_auto_submit }
}

export function mapApiToConvertedPerDistanceRate(
  rawLaneProposal: RawLaneProposal,
  distanceUnit: DistanceUnit
) {
  if (distanceUnit === 'miles') {
    return handleFalsyValues(rawLaneProposal.converted_per_mile_rate)
  } else {
    return handleFalsyValues(rawLaneProposal.converted_per_kilometer_rate)
  }
}

export function mapApiToPerDistanceRate(
  rawLaneProposal: RawLaneProposal,
  distanceUnit: DistanceUnit
) {
  if (distanceUnit === 'miles') {
    return rawLaneProposal.per_mile_rate
  } else {
    return rawLaneProposal.per_kilometer_rate
  }
}

function apiToLaneProposal(
  rawLaneProposal: RawLaneProposal,
  distanceUnit: DistanceUnit
): LaneProposal {
  return {
    id: rawLaneProposal.id,
    awardConfirmation: handleFalsyValues(rawLaneProposal.award_confirmation),
    awardedType: handleFalsyValues(rawLaneProposal.awarded_type),
    awardedVolume: rawLaneProposal.awarded_volume ?? undefined,
    capacity: rawLaneProposal.capacity,
    convertedFlatRate: handleFalsyValues(rawLaneProposal.converted_flat_rate),
    convertedPerDistanceRate: mapApiToConvertedPerDistanceRate(rawLaneProposal, distanceUnit),
    errors: mapApiToLaneProposalErrors(rawLaneProposal.errors),
    errorStr: handleFalsyValues(rawLaneProposal.error_str),
    flatRate: rawLaneProposal.flat_rate,
    laneId: rawLaneProposal.lane_id,
    negotiationNote: handleFalsyValues(rawLaneProposal.negotiation_note),
    perDistanceRate: mapApiToPerDistanceRate(rawLaneProposal, distanceUnit),
    underNegotiation: rawLaneProposal.under_negotiation,
    varianceFromAcceptanceRate: handleFalsyValues(rawLaneProposal.variance_from_acceptance_rate),
    lanePreferenceId: handleFalsyValues(rawLaneProposal.lane_preference),
  }
}

export function mapApiToLaneProposal(
  rawLaneProposal: RawLaneProposal | {},
  distanceUnit: DistanceUnit
): LaneProposal | undefined {
  if (isNotEmptyObject(rawLaneProposal)) {
    return apiToLaneProposal(rawLaneProposal, distanceUnit)
  }
}

export function mapApiToLaneProposals(
  rawLaneProposals: RawLaneProposal[] | [],
  distanceUnit: DistanceUnit
): LaneProposal[] | undefined {
  return rawLaneProposals.map(rawLaneProposal => apiToLaneProposal(rawLaneProposal, distanceUnit))
}

export function mapApiToLanePreferences(rawLanePreferences: RawLanePreference[]) {
  return rawLanePreferences.map(rawLanePreference => ({
    id: rawLanePreference.id,
    mode: rawLanePreference.mode,
    equipmentType: rawLanePreference.equipment_type,
    equipmentSize: rawLanePreference.equipment_size ?? undefined,
    subEquipmentType: rawLanePreference.subequipment_type ?? undefined,
    extraFields: rawLanePreference.extra_fields ?? {},
  }))
}

export function mapApiToDistance(rawLane: RawLane, distanceUnit: DistanceUnit) {
  if (distanceUnit === 'miles') {
    return rawLane.mileage
  } else {
    return rawLane.mileage_kilometers
  }
}

export function mapApiToLane(rawLane: RawLane, distanceUnit: DistanceUnit): Lane {
  return {
    id: rawLane.id,
    additionalStops: rawLane.additional_stops.map(mapApiToAdditionalStop),
    destCity: handleFalsyValues(rawLane.dest_city),
    destCountry: handleFalsyValues(rawLane.dest_country),
    destLatitude: handleFalsyValues(rawLane.dest_latitude),
    destLongitude: handleFalsyValues(rawLane.dest_longitude),
    destState: handleFalsyValues(rawLane.dest_state),
    destTimezone: handleFalsyValues(rawLane.dest_timezone),
    destZip: handleFalsyValues(rawLane.dest_zip),
    equipmentType: rawLane.equipment_type,
    equipmentSize: handleFalsyValues(rawLane.equipment_size),
    extraFields: isNotEmptyObject(rawLane.extra_fields) ? rawLane.extra_fields : undefined,
    laneId: rawLane.lane_id,
    laneProposal: mapApiToLaneProposal(rawLane.lane_proposal, distanceUnit),
    laneProposals: mapApiToLaneProposals(rawLane.lane_proposals, distanceUnit),
    preferences: mapApiToLanePreferences(rawLane.preferences),
    distance: mapApiToDistance(rawLane, distanceUnit),
    mileageSource: rawLane.mileage_source,
    notes: handleFalsyValues(rawLane.notes),
    originCity: handleFalsyValues(rawLane.origin_city),
    originCountry: handleFalsyValues(rawLane.origin_country),
    originLatitude: handleFalsyValues(rawLane.origin_latitude),
    originLongitude: handleFalsyValues(rawLane.origin_longitude),
    originState: handleFalsyValues(rawLane.origin_state),
    originTimezone: handleFalsyValues(rawLane.origin_timezone),
    originZip: handleFalsyValues(rawLane.origin_zip),
    previousProposal: rawLane.previous_proposal ?? undefined,
    stops: rawLane.stops.map(mapApiToStop),
    targetRange: handleFalsyValues(rawLane.target_range),
    volume: rawLane.volume,
  }
}

export function mapApiToAttachment(rawAttachment: RawAttachment): Attachment {
  return {
    id: rawAttachment.id,
    fileName: rawAttachment.file_name,
    url: rawAttachment.url,
  }
}

export function mapApiToBackgroundProcess(rawBackgroundProcess: RawBackgroundProcess | null) {
  return {
    process: rawBackgroundProcess?.process ?? undefined,
    result: rawBackgroundProcess?.result ?? undefined,
    status: rawBackgroundProcess?.status ?? undefined,
  }
}

export function mapApiToRequestForProposal(
  rawRequestForProposal: RawRequestForProposal
): RequestForProposal {
  return {
    id: rawRequestForProposal.id,
    attachments: rawRequestForProposal.attachments.map(mapApiToAttachment),
    canReceiveProposals: rawRequestForProposal.can_receive_proposals,
    capacityTimePeriod: rawRequestForProposal.capacity_time_period,
    contractEnd: rawRequestForProposal.contract_end,
    contractStart: rawRequestForProposal.contract_start,
    conversionRate: handleFalsyValues(rawRequestForProposal.conversion_rate),
    deleted: rawRequestForProposal.deleted,
    distanceUnit: rawRequestForProposal.distance_type,
    enableCarrierFeedback: rawRequestForProposal.enable_carrier_feedback,
    currency: rawRequestForProposal.currency,
    mode: rawRequestForProposal.mode,
    name: rawRequestForProposal.name,
    requestDeadline: rawRequestForProposal.request_deadline,
    shipperEmail: rawRequestForProposal.shipper_email,
    shipperLogo: handleFalsyValues(rawRequestForProposal.shipper_logo),
    shipperName: rawRequestForProposal.shipper_name,
    shipperPhone: rawRequestForProposal.shipper_phone,
    state: rawRequestForProposal.state,
    wasFinalized: rawRequestForProposal.was_finalized,
  }
}

export function mapApiToProposal(rawProposal: RawProposal): Proposal {
  return {
    accessorials: rawProposal.accessorials.map(rawAccessorial =>
      mapApiToAccessorial(rawAccessorial)
    ),
    requestedAccessorials: rawProposal.requested_accessorials.map(rawRequestedAccessorial => ({
      ...mapApiToPriceItemType(rawRequestedAccessorial),
      shipperNotes: rawRequestedAccessorial.shipper_notes || '',
    })),
    autoSubmit: mapApiToAutoSubmit(rawProposal).autoSubmit,
    awardConfirmationAutoSubmit:
      mapApiToAwardConfirmationAutoSubmit(rawProposal).awardConfirmationAutoSubmit,
    awardConfirmationDeadline: createDate(rawProposal.award_confirmation_deadline),
    awardFeedback: rawProposal.award_feedback ?? undefined,
    carrier: mapApiToCarrier(rawProposal.carrier),
    inviteUnderNegotiation: rawProposal.invite_under_negotiation,
    lanes: rawProposal.lanes.map(rawLane => mapApiToLane(rawLane, rawProposal.rfp.distance_type)),
    rateType: rawProposal.rate_type,
    rfp: mapApiToRequestForProposal(rawProposal.rfp),
    shipperCompanyName: rawProposal.shipper_company_name,
    state: rawProposal.state,
    submittedAt: createDate(rawProposal.submitted_at),
    suggestSignup: rawProposal.suggest_signup,
    unsubscribed: rawProposal.unsubscribed,
    backgroundProcess: mapApiToBackgroundProcess(rawProposal.background_process),
  }
}
